import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { AlertColor } from '@mui/material/Alert';
import { RootState } from '../../redux/Store';
import { POWER_STATES, RobotActions } from '../../utils/constants';

const LOW_BATTERY_PERCENTAGE_THRESHOLD = 10;
const CRITICAL_BATTERY_PERCENTAGE_THRESHOLD = 0;

export type BatteryNotificationDevice = 'driveBase' | 'controller';
export type BatteryNotificationId = 'drivebase-low-battery' | 'drivebase-critical-battery' | 'controller-low-battery' | 'controller-critical-battery';

export interface BatteryNotification {
  device: BatteryNotificationDevice;
  id: BatteryNotificationId;
  message: string;
  severity: AlertColor;
}

interface UseBatteryNotificationsResult {
  notifications: BatteryNotification[];
  onClose: (id: BatteryNotificationId) => void;
}

const isLowBatteryPercentage = (percentage: number | null | undefined) =>
  percentage != null && percentage > CRITICAL_BATTERY_PERCENTAGE_THRESHOLD && percentage <= LOW_BATTERY_PERCENTAGE_THRESHOLD;

const isCriticalBatteryPercentage = (percentage: number | null | undefined) =>
  percentage != null && percentage <= CRITICAL_BATTERY_PERCENTAGE_THRESHOLD;

const areNotificationsEqual = (a: BatteryNotification[], b: BatteryNotification[]) =>
  a.length === b.length &&
  a.every((notification, index) => {
    const nextNotification = b[index];
    return (
      notification.device === nextNotification.device &&
      notification.id === nextNotification.id &&
      notification.message === nextNotification.message &&
      notification.severity === nextNotification.severity
    );
  });

export const useBatteryNotifications = (): UseBatteryNotificationsResult => {
  const { t } = useTranslation();
  const { robotState, batteryCharging, batteryPercentage, powerState } = useSelector((state: RootState) => state.robot);
  const { controllerConnected } = useSelector((state: RootState) => state.application);
  const { batteryCharging: controllerBatteryCharging, batteryPercentage: controllerBatteryPercentage } = useSelector(
    (state: RootState) => state.controllerData
  );

  const previouslyActiveIdsRef = useRef<Set<BatteryNotificationId>>(new Set());
  const [notifications, setNotifications] = useState<BatteryNotification[]>([]);

  const activeNotifications = useMemo(() => {
    const nextNotifications: BatteryNotification[] = [];

    const isDriveBaseBatteryNotificationEligible = robotState === RobotActions.STATE.CONNECTED && !batteryCharging;
    const isDriveBaseCriticalBattery =
      isDriveBaseBatteryNotificationEligible && (powerState === POWER_STATES.CRITICAL || isCriticalBatteryPercentage(batteryPercentage));
    const isDriveBaseLowBattery =
      isDriveBaseBatteryNotificationEligible &&
      !isDriveBaseCriticalBattery &&
      (powerState === POWER_STATES.LOW || isLowBatteryPercentage(batteryPercentage));

    // BPRDU firmware owns critical-battery disconnect and shutdown. Keep this
    // notification passive; do not force an app-level disconnect from here.
    if (isDriveBaseCriticalBattery) {
      nextNotifications.push({
        device: 'driveBase',
        id: 'drivebase-critical-battery',
        message: t('batterySnackbar.driveBaseCritical', {
          defaultValue: 'Drive Base battery is critically low. Charge your Drive Base now.',
        }),
        severity: 'error',
      });
    }

    if (isDriveBaseLowBattery) {
      nextNotifications.push({
        device: 'driveBase',
        id: 'drivebase-low-battery',
        message: t('batterySnackbar.driveBaseLow', {
          defaultValue: 'Drive Base battery is low. Charge your Drive Base soon.',
        }),
        severity: 'warning',
      });
    }

    const isControllerBatteryNotificationEligible = controllerConnected && !controllerBatteryCharging;
    const isControllerCriticalBattery = isControllerBatteryNotificationEligible && isCriticalBatteryPercentage(controllerBatteryPercentage);
    const isControllerLowBattery =
      isControllerBatteryNotificationEligible && !isControllerCriticalBattery && isLowBatteryPercentage(controllerBatteryPercentage);

    if (isControllerCriticalBattery) {
      nextNotifications.push({
        device: 'controller',
        id: 'controller-critical-battery',
        message: t('batterySnackbar.controllerCritical', {
          defaultValue: 'Controller battery is critically low. Charge your controller now.',
        }),
        severity: 'error',
      });
    }

    if (isControllerLowBattery) {
      nextNotifications.push({
        device: 'controller',
        id: 'controller-low-battery',
        message: t('batterySnackbar.controllerLow', {
          defaultValue: 'Controller battery is low. Charge your controller soon.',
        }),
        severity: 'warning',
      });
    }

    return nextNotifications;
  }, [batteryCharging, batteryPercentage, controllerBatteryCharging, controllerBatteryPercentage, controllerConnected, powerState, robotState, t]);

  useEffect(() => {
    const activeIds = new Set(activeNotifications.map((notification) => notification.id));
    const previouslyActiveIds = previouslyActiveIdsRef.current;

    setNotifications((currentNotifications) => {
      const nextNotifications = currentNotifications
        .filter((notification) => activeIds.has(notification.id))
        .map((notification) => activeNotifications.find((activeNotification) => activeNotification.id === notification.id) ?? notification);

      activeNotifications.forEach((notification) => {
        const alreadyVisible = nextNotifications.some((visibleNotification) => visibleNotification.id === notification.id);
        const justBecameActive = !previouslyActiveIds.has(notification.id);

        if (justBecameActive && !alreadyVisible) {
          nextNotifications.push(notification);
        }
      });

      return areNotificationsEqual(currentNotifications, nextNotifications) ? currentNotifications : nextNotifications;
    });

    previouslyActiveIdsRef.current = activeIds;
  }, [activeNotifications]);

  return {
    notifications,
    onClose: (id: BatteryNotificationId) => {
      setNotifications((currentNotifications) => currentNotifications.filter((notification) => notification.id !== id));
    },
  };
};

export default useBatteryNotifications;
